import { TelemetryLoggerService } from "./logger-service";
import { nonStandardPushInformationEvent } from "../../ServiceDesk/sdServices/non-standard-logging";
import { v4 } from "uuid";
import * as Jsll from "./jsll-types";

declare var awa: any;

export type UnknownEventWithSDUniqueId = { __sd_unique_id: string };
export type CustomEventWithSDUniqueId = Jsll.ICustomEvent & UnknownEventWithSDUniqueId;
export const telemetryMinimumLifetimeInterval = 20000;
export const oldEventStorageKey = "OldEventStorage_V1";
export type OutstandingEventsContract = { [id: string]: { event: CustomEventWithSDUniqueId } };

export class AwaEventTracker {
  private outstandingEvents: OutstandingEventsContract = {};
  private constructedAt: Date = new Date();

  private oldEventStorage: OutstandingEventsContract;
  private loggerService: TelemetryLoggerService;
  private isInitialized = false;

  // This class follows a strange initialization flow -- it expects the loggerService to call initialize. This was mainly
  //  done to decouple the two pieces of functionality.
  public initialize(loggerService: TelemetryLoggerService) {
    if (!this.isInitialized) {
      this.loggerService = loggerService;
      this.registerUniqueIdPropagater();
      this.registerVortexCompletionEvents();
      this.registerUnloadListenerToStoreUnconfirmedEvents();
      this.isInitialized = true;
      this.processOldEvents();
    }
  }

  public trackEvent(event: CustomEventWithSDUniqueId) {
    this.ensureInitialized();
    let uniqueId = (event.__sd_unique_id = event.__sd_unique_id || v4());
    this.outstandingEvents[uniqueId] = { event: event };
  }

  public getNumberOfOldEvents(): number {
    this.ensureInitialized();
    return this.oldEventStorage ? Object.keys(this.oldEventStorage).length : 0;
  }

  private registerVortexCompletionEvents() {
    let localTracker = this;
    (<any>awa).OnSuccessfulVortexRequest = function (
      src: any,
      event: UnknownEventWithSDUniqueId | UnknownEventWithSDUniqueId[]
    ) {
      if (event != undefined) {
        localTracker.cleanEventOrEvents(event);
      }
    };

    (<any>awa).OnFailedVortexRequest = function (
      errorObject: any,
      event: UnknownEventWithSDUniqueId | UnknownEventWithSDUniqueId[]
    ) {
      if (event != undefined) {
        localTracker.cleanEventOrEvents(event);
      }
    };
  }

  private cleanEventOrEvents(eventOrEvents: UnknownEventWithSDUniqueId[] | UnknownEventWithSDUniqueId) {
    if (Array.isArray(eventOrEvents)) {
      eventOrEvents.forEach((eachEvent) => {
        this.cleanIndividualEvent(eachEvent);
      });
    } else {
      this.cleanIndividualEvent(eventOrEvents);
    }
  }

  private cleanIndividualEvent(eachEvent: UnknownEventWithSDUniqueId) {
    if (eachEvent.__sd_unique_id) {
      delete this.outstandingEvents[eachEvent.__sd_unique_id];
    }
  }

  private registerUniqueIdPropagater() {
    let oldValidate = (<any>awa)._validateAndTranslateEvent;
    (<any>awa)._validateAndTranslateEvent = function (event: UnknownEventWithSDUniqueId) {
      let sdUnique = event.__sd_unique_id;
      let translatedEvent = oldValidate(event);
      if (sdUnique) {
        translatedEvent.event.__sd_unique_id = sdUnique;
      }

      return translatedEvent;
    };
  }

  private registerUnloadListenerToStoreUnconfirmedEvents() {
    window.addEventListener("unload", () => {
      if (
        new Date().getTime() - this.constructedAt.getTime() > telemetryMinimumLifetimeInterval &&
        this.oldEventStorage
      ) {
        Object.keys(this.oldEventStorage).forEach((key) => {
          delete this.outstandingEvents[key];
        });
      }
      localStorage.setItem(oldEventStorageKey, JSON.stringify(this.outstandingEvents));
    });
  }

  private processOldEvents() {
    try {
      let oldEventStorage = localStorage.getItem(oldEventStorageKey);
      if (oldEventStorage) {
        this.oldEventStorage = JSON.parse(oldEventStorage);
        Object.keys(this.oldEventStorage).forEach((key) => {
          this.loggerService.pushEvent(this.oldEventStorage[key].event);
        });
        awa.vortexEvents.batchQueuedEvents();
      }
    } catch (error) {
      localStorage.removeItem(oldEventStorageKey);
      nonStandardPushInformationEvent(
        "process-old-events",
        JSON.stringify({ message: "Could not load old events, removing old key and moving on.", data: error })
      );
    }
  }

  private ensureInitialized() {
    if (!this.isInitialized) {
      throw Error("The awa event tracker is being used while not yet being initialized.");
    }
  }
}
